<template>
  <div class="event-signup-page">
    <a-card :bordered="false">
      <a-spin :spinning="loading">
        <h1>{{ event.eventName || '活动报名' }}</h1>
        <div class="event-time">{{ event.startTime }} 至 {{ event.endTime }}</div>
        <a-alert v-if="signup && signup.auditComment" type="info" show-icon :message="'审核批注：' + signup.auditComment" style="margin:16px 0" />
        <a-alert v-if="signup && Number(signup.auditStatus) === 4" type="info" show-icon message="当前为草稿，提交后才会进入审核。" style="margin:16px 0" />
        <a-steps v-if="hasSections" :current="currentStep" size="small" class="signup-steps">
          <a-step v-for="section in formSections" :key="section.id" :title="section.fieldName" />
        </a-steps>
        <a-form-model :label-col="{ span: 5 }" :wrapper-col="{ span: 14 }">
          <div v-if="hasSections && currentSection" class="section-heading">{{ currentSection.fieldName }}</div>
          <a-form-model-item
            v-for="field in displayFields"
            :key="field.id"
            :label="field.fieldName"
            :required="field.requiredFlag === 1"
          >
            <a-input
              v-if="field.fieldType === 'text'"
              v-model="fieldModel[field.id].fieldValue"
              :placeholder="field.placeholder"
              :disabled="!canEdit"
            />
            <a-textarea
              v-else-if="field.fieldType === 'textarea'"
              v-model="fieldModel[field.id].fieldValue"
              :placeholder="field.placeholder"
              :rows="4"
              :disabled="!canEdit"
            />
            <div v-else-if="field.fieldType === 'date'">
              <j-date
                v-model="fieldModel[field.id].fieldValue"
                date-format="YYYY-MM-DD"
                :disabled="!canEdit"
                style="width: 100%"
              />
              <div v-if="field.placeholder" class="field-tip">{{ field.placeholder }}</div>
            </div>
            <div v-else-if="field.fieldType === 'radio'">
              <a-radio-group v-model="fieldModel[field.id].fieldValue" :disabled="!canEdit">
                <a-radio v-for="option in parseOptions(field.fieldOptions)" :key="option.key" :value="option.key">
                  {{ option.value }}
                </a-radio>
              </a-radio-group>
              <a-input
                v-if="field.allowOtherFlag === 1"
                v-model="fieldModel[field.id].otherValue"
                :disabled="!canEdit"
                placeholder="其他"
                style="margin-top:8px"
              />
              <div v-if="field.placeholder" class="field-tip">{{ field.placeholder }}</div>
            </div>
            <div v-else-if="field.fieldType === 'checkbox'">
              <a-checkbox-group v-model="fieldModel[field.id].fieldValueArray" :disabled="!canEdit">
                <a-checkbox v-for="option in parseOptions(field.fieldOptions)" :key="option.key" :value="option.key">
                  {{ option.value }}
                </a-checkbox>
              </a-checkbox-group>
              <a-input
                v-if="field.allowOtherFlag === 1"
                v-model="fieldModel[field.id].otherValue"
                :disabled="!canEdit"
                placeholder="其他"
                style="margin-top:8px"
              />
              <div v-if="field.placeholder" class="field-tip">{{ field.placeholder }}</div>
            </div>
            <div v-else-if="field.fieldType === 'file'">
              <j-upload
                v-model="fieldModel[field.id].fieldValue"
                :disabled="!canEdit"
                uploadPath="eventSignup/"
              />
              <div v-if="field.placeholder" class="field-tip">{{ field.placeholder }}</div>
            </div>
          </a-form-model-item>
        </a-form-model>
        <div class="actions">
          <a-button @click="$router.go(-1)">返回</a-button>
          <a-button v-if="hasSections" :disabled="currentStep === 0" @click="prevStep">上一步</a-button>
          <a-button v-if="hasSections" :disabled="currentStep >= formSections.length - 1" @click="nextStep">下一步</a-button>
          <a-button :disabled="!canEdit" :loading="savingDraft" @click="saveDraft">保存草稿</a-button>
          <a-button type="primary" :disabled="!canEdit" :loading="submitting" @click="submitSignup">提交报名</a-button>
        </div>
      </a-spin>
    </a-card>
  </div>
</template>

<script>
import { getAction, httpAction } from '@/api/manage'
import JUpload from '@/components/jeecg/JUpload'
import JDate from '@/components/jeecg/JDate'

const VALIDATION_PATTERNS = {
  mobile: '1[3-9]\\d{9}',
  idcard: '(?:\\d{15}|\\d{17}[\\dXx])',
  email: '[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}',
  number: '\\d+'
}

const VALIDATION_MESSAGES = {
  mobile: '请输入正确的手机号',
  idcard: '请输入正确的身份证号',
  email: '请输入正确的邮箱',
  number: '请输入纯数字'
}

export default {
  name: 'EventSignup',
  components: { JUpload, JDate },
  data() {
    return {
      loading: false,
      submitting: false,
      savingDraft: false,
      currentStep: 0,
      event: {},
      fields: [],
      signup: null,
      values: [],
      canEdit: true,
      fieldModel: {}
    }
  },
  mounted() {
    this.loadData()
  },
  watch: {
    '$route.query.id': {
      immediate: false,
      handler() {
        this.event = {}
        this.fields = []
        this.signup = null
        this.values = []
        this.canEdit = true
        this.fieldModel = {}
        this.currentStep = 0
        this.loadData()
      }
    }
  },
  computed: {
    formSections() {
      const sections = []
      let current = null
      this.fields.forEach((field, index) => {
        if (field.fieldType === 'section') {
          current = {
            id: field.id || `section_${index}`,
            fieldName: field.fieldName || `第${sections.length + 1}步`,
            fields: []
          }
          sections.push(current)
          return
        }
        if (!current) {
          current = {
            id: 'default',
            fieldName: '报名信息',
            fields: []
          }
          sections.push(current)
        }
        current.fields.push(field)
      })
      return sections.length ? sections : [{ id: 'default', fieldName: '报名信息', fields: [] }]
    },
    hasSections() {
      return this.fields.some(field => field.fieldType === 'section')
    },
    currentSection() {
      return this.formSections[this.currentStep] || this.formSections[0]
    },
    displayFields() {
      if (this.hasSections) {
        return this.currentSection ? this.currentSection.fields : []
      }
      return this.fields.filter(field => field.fieldType !== 'section')
    }
  },
  methods: {
    loadData() {
      const eventId = this.$route.query.id
      if (!eventId) {
        this.$message.warning('参数错误')
        this.$router.go(-1)
        return
      }
      this.loading = true
      getAction('/xjtech/eventSignup/myDetail', { eventId }).then(res => {
        if (res.success) {
          const result = res.result || {}
          this.event = result.event || {}
          this.fields = result.fields || []
          this.signup = result.signup || null
          this.values = result.values || []
          this.canEdit = result.canEdit !== false
          this.initFieldModel()
          this.currentStep = 0
        } else {
          this.$message.warning(res.message || '获取报名表失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    initFieldModel() {
      const valueMap = {}
      this.values.forEach(item => {
        valueMap[item.fieldCode] = item
      })
      this.fieldModel = {}
      this.fields.forEach(field => {
        if (field.fieldType === 'section') return
        const value = valueMap[field.fieldCode] || {}
        this.$set(this.fieldModel, field.id, {
          fieldValue: value.fieldValue || '',
          fieldValueArray: value.fieldValue ? value.fieldValue.split(',').filter(Boolean) : [],
          otherValue: value.otherValue || '',
          fileInfo: value.fileInfo || ''
        })
      })
    },
    saveDraft() {
      const payload = {
        id: this.signup && this.signup.id,
        eventId: this.event.id,
        values: this.buildValues()
      }
      this.savingDraft = true
      httpAction('/xjtech/eventSignup/saveDraft', payload, 'post').then(res => {
        if (res.success) {
          const result = res.result || {}
          this.signup = result.signup || this.signup
          this.values = result.values || this.values
          this.$message.success('草稿已保存')
        } else {
          this.$message.warning(res.message || '保存草稿失败')
        }
      }).finally(() => {
        this.savingDraft = false
      })
    },
    submitSignup() {
      if (!this.validateForm()) return
      const payload = {
        id: this.signup && this.signup.id,
        eventId: this.event.id,
        values: this.buildValues()
      }
      this.submitting = true
      const method = this.signup ? 'put' : 'post'
      const url = this.signup ? '/xjtech/eventSignup/updateMine' : '/xjtech/eventSignup/submit'
      httpAction(url, payload, method).then(res => {
        if (res.success) {
          this.$message.success('提交成功')
          this.$router.push({ path: '/personal', query: { tab: 'eventSignup' } })
        } else {
          this.$message.warning(res.message || '提交失败')
        }
      }).finally(() => {
        this.submitting = false
      })
    },
    validateForm() {
      for (const field of this.fields.filter(item => item.fieldType !== 'section')) {
        const model = this.fieldModel[field.id] || {}
        if (field.requiredFlag === 1 && this.isFieldEmpty(field, model)) {
          this.focusFieldStep(field.id)
          this.$message.warning(`${field.fieldName}不能为空`)
          return false
        }
        const validationMessage = this.validateFieldValue(field, model.fieldValue)
        if (validationMessage) {
          this.focusFieldStep(field.id)
          this.$message.warning(validationMessage)
          return false
        }
      }
      return true
    },
    isFieldEmpty(field, model) {
      if (field.fieldType === 'checkbox') {
        return (!model.fieldValueArray || model.fieldValueArray.length === 0) && !model.otherValue
      }
      if (field.fieldType === 'file') {
        return !model.fieldValue && !model.fileInfo
      }
      return !model.fieldValue && !model.otherValue
    },
    validateFieldValue(field, value) {
      if (!value) return ''
      if (field.fieldType === 'date') {
        return this.isValidDate(value) ? '' : `${field.fieldName}日期格式应为YYYY-MM-dd`
      }
      if (field.fieldType !== 'text' || !field.validationType) return ''
      const pattern = field.validationType === 'custom'
        ? field.validationPattern
        : VALIDATION_PATTERNS[field.validationType]
      if (!pattern) return `${field.fieldName}校验规则配置错误`
      try {
        const valid = new RegExp(`^(?:${pattern})$`).test(value)
        return valid ? '' : (VALIDATION_MESSAGES[field.validationType] || `${field.fieldName}格式不正确`)
      } catch (e) {
        return `${field.fieldName}校验规则配置错误`
      }
    },
    isValidDate(value) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
      if (!match) return false
      const year = Number(match[1])
      const month = Number(match[2])
      const day = Number(match[3])
      const date = new Date(Date.UTC(year, month - 1, day))
      return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    },
    buildValues() {
      return this.fields.filter(field => field.fieldType !== 'section').map(field => {
        const model = this.fieldModel[field.id] || {}
        const fieldValue = field.fieldType === 'checkbox'
          ? (model.fieldValueArray || []).join(',')
          : model.fieldValue
        return {
          fieldId: field.id,
          fieldValue,
          fieldText: fieldValue,
          otherValue: model.otherValue,
          fileInfo: model.fileInfo
        }
      })
    },
    parseOptions(options) {
      if (!options) return []
      try {
        return JSON.parse(options)
      } catch (e) {
        return []
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep -= 1
      }
    },
    nextStep() {
      if (this.currentStep < this.formSections.length - 1) {
        this.currentStep += 1
      }
    },
    focusFieldStep(fieldId) {
      if (!this.hasSections) return
      const index = this.formSections.findIndex(section => section.fields.some(field => field.id === fieldId))
      if (index >= 0) {
        this.currentStep = index
      }
    }
  }
}
</script>

<style scoped lang="less">
.event-signup-page {
  max-width: 960px;
  margin: 0 auto;
}
h1 {
  margin-bottom: 8px;
}
.event-time {
  color: #666;
  margin-bottom: 24px;
}
.signup-steps {
  margin: 20px 0 28px;
}
.section-heading {
  margin: 8px 0 24px;
  padding: 10px 14px;
  color: #1f2f3d;
  font-weight: 600;
  background: #f5f7fa;
  border-left: 4px solid #1890ff;
}
.field-tip {
  margin-top: 6px;
  color: #f5222d;
  line-height: 1.5;
}
.actions {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
}
</style>
